import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/select';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, Clock, AlertTriangle, Users as UsersIcon } from 'lucide-react';
import { User } from '../models/User';
import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from 'react';
import { Team } from '../models/Team';
import { TeamService } from '../services/TeamService';
import api from '../api';
import '../styles/globals.css';

interface ReportsProps {
  user: User;
}

export function Reports({ user }: ReportsProps) {
  const params = useParams();
  const teamIdParam = params.teamId;
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<null | {
    teamId: number;
    attendanceRate: number;
    latenessRate: number;
    weeklyAverageHours: number;
    overtimeHours: number;
    teamUserCount: number;
    weeklyAverageHoursPerUser: number;
    daysPresentCount: number;
    expectedDailyMinutes: number;
    userDailyDetails: any | null;
    userAttendanceRates: Record<string, number>;
    userLatenessRates: Record<string, number>;
    userOvertimeHours: Record<string, number>;
    teamComparisonData?: any[];
    monthlyTrendData?: any[];
  }>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(teamIdParam ? Number(teamIdParam) : null);
  
  // Helper to format percents whether the backend sends 0-1 or 0-100
  const formatPercent = (v: number) => {
    if (v == null || isNaN(v)) return '--';
    return v <= 1 ? `${(v * 100).toFixed(1)}%` : `${v.toFixed(1)}%`;
  };

  // Period selection state: week|month|quarter|year
  type Period = 'week' | 'month' | 'quarter' | 'year';
  const [period, setPeriod] = useState<Period>('week');
  const [range, setRange] = useState<{ startDate: string; endDate: string } | null>(null);

  useEffect(() => {
    // load teams for the current user (manager) so we can select which team to show
    const loadTeams = async () => {
      try {
        if (user) {
          const t = await TeamService.getTeamsByManagerId(user.id);
          setTeams(t || []);
          // if no team selected via param, pick the first
          if (!teamIdParam && (!selectedTeamId || selectedTeamId === null) && (t && t.length > 0)) {
            const firstId = t[0].id;
            setSelectedTeamId(firstId);
            // navigate to the canonical URL
            navigate(`/report/${firstId}`, { replace: true });
          }
        }
      } catch (e) {
        // ignore load errors here; Reports page will show its own errors
      }
    };
    loadTeams();

    const fetchReport = async () => {
      const effectiveTeamId = selectedTeamId ?? (teamIdParam ? Number(teamIdParam) : null);
      if (!effectiveTeamId) return;
      setLoading(true);
      setError(null);
      try {
  // compute startDate/endDate for the selected period
        const now = new Date();
        let start: Date, end: Date;
        if (period === 'week') {
          const day = now.getDay();
          const diffToMonday = day === 0 ? -6 : 1 - day;
          start = new Date(now);
          start.setDate(now.getDate() + diffToMonday);
          start.setHours(0, 0, 0, 0);
          end = new Date(start);
          end.setDate(start.getDate() + 6);
          end.setHours(23, 59, 59, 999);
        } else if (period === 'month') {
          start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
          end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
        } else if (period === 'quarter') {
          const quarter = Math.floor(now.getMonth() / 3);
          start = new Date(now.getFullYear(), quarter * 3, 1);
          end = new Date(now.getFullYear(), quarter * 3 + 3, 0, 23, 59, 59, 999);
        } else {
          start = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
          end = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
        }
        const toIsoDate = (d: Date) => {
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${y}-${m}-${day}`;
        };
  const startDate = toIsoDate(start);
  const endDate = toIsoDate(end);
        // Include startDate/endDate as query params so the backend returns KPIs for the selected period
  const { data } = await api.get(`/reports?teamId=${effectiveTeamId}&withDailyDetails=true&startDate=${startDate}&endDate=${endDate}`);
  // Debug log to inspect backend payload when needed
  // eslint-disable-next-line no-console
  console.debug('Fetched report', data);
  setRange({ startDate, endDate });
  setReport(data);
      } catch (e: any) {
        console.error('Erreur fetching report', e);
        setError(e?.response?.status === 404 ? 'Équipe non trouvée (404)' : (e?.response?.data || e?.message || 'Erreur lors du chargement du rapport'));
      } finally {
        setLoading(false);
      }
    };

  fetchReport();
  }, [teamIdParam, period, selectedTeamId, user, navigate]);

  // If report is available, map values to display variables used below
  const kpiCardsFromReport = report
    ? [
        {
          title: 'Taux de présence',
          value: formatPercent(report.attendanceRate),
          change: '',
          trend: report.attendanceRate >= 0 ? 'up' : 'down',
          icon: UsersIcon,
          color: 'text-green-600'
        },
        {
          title: 'Heures supplémentaires',
          value: `${report.overtimeHours?.toFixed?.(1) ?? report.overtimeHours ?? 0}h`,
          change: '',
          trend: 'up',
          icon: Clock,
          color: 'text-blue-600'
        },
        {
          title: 'Taux de retard',
          value: formatPercent(report.latenessRate),
          change: '',
          trend: report.latenessRate >= 0 ? 'down' : 'up',
          icon: AlertTriangle,
          color: 'text-orange-600'
        },
        {
          title: 'Moyenne hebdo',
          value: `${report.weeklyAverageHours?.toFixed?.(1) ?? 0}h`,
          change: '',
          trend: 'up',
          icon: TrendingUp,
          color: 'text-purple-600'
        }
      ]
  : [];

  // Build presence pie from report data if present
  const presenceDataFromReport = report
    ? (() => {
        const attendance = Number(report.attendanceRate ?? 0); // expected 0-100
        const lateness = Number(report.latenessRate ?? 0); // expected 0-100 (percent of present days that are late)
        // absolute late percentage of total days = attendance * (lateness/100)
        const absoluteLate = Math.round((attendance * lateness) / 100);
        const onTime = Math.max(0, Math.round(attendance - absoluteLate));
        const absent = Math.max(0, Math.round(100 - attendance));
        return [
          { name: "À l'heure", value: onTime, color: '#22c55e' },
          { name: 'En retard', value: absoluteLate, color: '#f97316' },
          { name: 'Absent', value: absent, color: '#ef4444' }
        ];
      })()
  : [];

  // Example weekly hours: if backend supplies userDailyDetails, try to map it; otherwise fall back to mock
  const weeklyHoursDataFromReport = (() => {
  if (!report || !report.userDailyDetails) {
    // If we have a requested range, return zeros for every date in the range so the chart shows the full period.
    if (range) {
    // cible: expected hours per day for the whole team = expectedDailyMinutes/60 * teamUserCount
    const teamCount = report?.teamUserCount ?? 1;
    const cible = Math.round(((report?.expectedDailyMinutes ?? 420) / 60) * teamCount);
      const dateArray: string[] = [];
      const s = new Date(range.startDate + 'T00:00:00');
      const e = new Date(range.endDate + 'T00:00:00');
      for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        dateArray.push(`${y}-${m}-${day}`);
      }
      return dateArray.map((dateStr) => {
        const jsDate = new Date(dateStr + 'T00:00:00');
        let label = jsDate.toLocaleDateString('fr-FR', { weekday: 'short' });
        if (period === 'month' || period === 'quarter' || period === 'year') {
          label = jsDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'numeric' });
        }
        return { day: label, heures: 0, cible };
      });
    }
    return [];
  }

  const totalsByDate: Record<string, { hours: number; label: string }> = {};
  
    Object.entries(report.userDailyDetails).forEach(([userId, details]) => {
      if (!Array.isArray(details)) return;
      details.forEach((d: any) => {
        // d.date might be a string "YYYY-MM-DD" or an object { year, month, day }
        let dateStr: string | null = null;
        if (!d) return;
        if (typeof d.date === 'string') dateStr = d.date;
        else if (d.date && typeof d.date === 'object' && ('year' in d.date)) {
          const y = d.date.year;
          const m = String(d.date.month).padStart(2, '0');
          const day = String(d.date.day).padStart(2, '0');
          dateStr = `${y}-${m}-${day}`;
        }
        if (!dateStr) return;
        // Use totalHours if present, otherwise fallback to totalMinutes/60, sum of session durations,
        // or derive from session start/end timestamps. Be permissive about number-as-string values.
        let hours = 0;
        const tryNumber = (v: any): number | null => {
          if (v == null) return null;
          if (typeof v === 'number' && !isNaN(v)) return v;
          if (typeof v === 'string') {
            const trimmed = v.trim();
            if (trimmed === '') return null;
            const n = Number(trimmed);
            if (!isNaN(n)) return n;
          }
          return null;
        };

        const th = tryNumber(d.totalHours ?? d.hours ?? d.totalHour);
        if (th != null) {
          hours = th;
        } else {
          const tm = tryNumber(d.totalMinutes ?? d.minutes ?? d.totalMinute);
          if (tm != null) {
            hours = tm / 60;
          } else if (Array.isArray(d.sessions) && d.sessions.length > 0) {
            // Try duration fields first
            const totalMinutesFromSessions = d.sessions.reduce((acc: number, s: any) => {
              const v = tryNumber(s.durationMinutes ?? s.duration ?? s.durationInMinutes ?? s.minutes);
              return acc + (v ?? 0);
            }, 0);
            if (totalMinutesFromSessions > 0) {
              hours = totalMinutesFromSessions / 60;
            } else {
              // Fallback: try start/end timestamps
              const totalMs = d.sessions.reduce((acc: number, s: any) => {
                const start = s.start ? Date.parse(s.start) : NaN;
                const end = s.end ? Date.parse(s.end) : NaN;
                if (!isNaN(start) && !isNaN(end) && end > start) return acc + (end - start);
                return acc;
              }, 0);
              if (totalMs > 0) hours = totalMs / (1000 * 60 * 60);
            }
          }
        }
        if (!totalsByDate[dateStr]) {
          // create a short weekday label in French locale (Mon -> Lun)
          const jsDate = new Date(dateStr + 'T00:00:00');
          const label = jsDate.toLocaleDateString('fr-FR', { weekday: 'short' });
          totalsByDate[dateStr] = { hours: 0, label };
        }
        totalsByDate[dateStr].hours += hours;
      });
    });

    // eslint-disable-next-line no-console
    console.debug('Computed totalsByDate', totalsByDate);

    // If we have a requested range, produce an entry for every date in that range (inclusive)
  const teamCount = report?.teamUserCount ?? 1;
  const cible = Math.round(((report?.expectedDailyMinutes ?? 420) / 60) * teamCount);
    if (range) {
      const dateArray: string[] = [];
      const s = new Date(range.startDate + 'T00:00:00');
      const e = new Date(range.endDate + 'T00:00:00');
      for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        dateArray.push(`${y}-${m}-${day}`);
      }

      const final = dateArray.map((dateStr) => {
        const entry = totalsByDate[dateStr];
        const jsDate = new Date(dateStr + 'T00:00:00');
        let label = jsDate.toLocaleDateString('fr-FR', { weekday: 'short' });
        
        if (period === 'month') {
          label = jsDate.toLocaleDateString('fr-FR', { day: 'numeric' });
        } else if (period === 'quarter' || period === 'year') {
           label = jsDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'numeric' });
        }

        // If specific label from default logic was desired, we could use it, but overriding for consistency is better
        if (entry && period === 'week') {
           label = entry.label;
        }

        const hours = entry ? Number(entry.hours.toFixed(1)) : 0;
        return { day: label, heures: hours, cible };
      });

      // eslint-disable-next-line no-console
      console.debug('weeklyHoursDataFromReport (final)', final);
      return final;
    }

    // Fallback: use any available dates sorted
    const sortedDates = Object.keys(totalsByDate).sort();
    if (sortedDates.length === 0) return [];
    return sortedDates.map((dateStr) => ({ day: totalsByDate[dateStr].label, heures: Number(totalsByDate[dateStr].hours.toFixed(1)), cible }));
  })();

  const teamComparisonData = report && Array.isArray(report.teamComparisonData) && report.teamComparisonData.length > 0
    ? report.teamComparisonData.map((r: any) => ({ 
        equipe: r.equipe ?? r.teamName ?? 'Equipe', 
        heures: Number((Number(r.heures ?? 0)).toFixed(1)), 
        moyenne: Number((Number(r.moyenne ?? 0)).toFixed(1)) 
      }))
    : [];

  const monthlyTrendData = report && Array.isArray(report.monthlyTrendData) && report.monthlyTrendData.length > 0
    ? report.monthlyTrendData.map((r: any) => ({ 
        mois: r.mois ?? r.month ?? '', 
        heures: Number((Number(r.heures ?? 0)).toFixed(1)), 
        retards: Number(r.retards ?? 0) 
      }))
    : [];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#0f172a', borderBottomColor: '#9ca3af' }}></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Erreur</CardTitle>
            <CardDescription>Impossible de charger le rapport</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-red-600">{String(error)}</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1>Rapports & Analyses</h1>
          <p className="text-muted-foreground">
            Visualisez les performances et les tendances de vos équipes
          </p>
        </div>
        <div className="flex items-center gap-4 parameters-select">
          <Select value={selectedTeamId ? String(selectedTeamId) : ''} onValueChange={(v) => {
            const id = Number(v);
            setSelectedTeamId(id);
            navigate(`/report/${id}`);
          }}>
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Sélectionner une équipe" />
            </SelectTrigger>
            <SelectContent>
              {teams.map((t) => (
                <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={period} onValueChange={(v) => setPeriod(v as Period)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Période" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Cette semaine</SelectItem>
              <SelectItem value="month">Ce mois</SelectItem>
              <SelectItem value="quarter">Ce trimestre</SelectItem>
              <SelectItem value="year">Cette année</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
  {kpiCardsFromReport.map((kpi: any, index: number) => {
          const Icon = kpi.icon;
          const TrendIcon = kpi.trend === 'up' ? TrendingUp : TrendingDown;
          
          return (
            <Card key={index}>
              <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                <CardTitle className="text-sm text-muted-foreground">
                  {kpi.title}
                </CardTitle>
                <Icon className={`h-4 w-4 ${kpi.color}`} />
              </CardHeader>
              <CardContent>
                <div className="text-2xl">{kpi.value}</div>
                <div className="flex items-center gap-1 mt-1">
                  <TrendIcon className={`h-3 w-3 ${kpi.trend === 'up' ? 'text-green-600' : 'text-red-600'}`} />
                  <span className={`text-xs ${kpi.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                    {kpi.change}
                  </span>
                  <span className="text-xs text-muted-foreground">vs semaine dernière</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts Section */}
      <Tabs defaultValue="hours" className="space-y-4">
        <TabsList className="reports-tabs">
          <TabsTrigger value="hours">Heures travaillées</TabsTrigger>
          <TabsTrigger value="presence">Présence</TabsTrigger>
          <TabsTrigger value="teams">Comparaison équipes</TabsTrigger>
          <TabsTrigger value="trends">Tendances</TabsTrigger>
        </TabsList>

        <TabsContent value="hours" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Heures travaillées par jour</CardTitle>
              <CardDescription>Comparaison avec l'objectif hebdomadaire</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={weeklyHoursDataFromReport}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="heures" fill="#0f172a" name="Heures réelles" />
                  <Bar dataKey="cible" fill="#9ca3af" name="Objectif" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="presence" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Répartition de la présence</CardTitle>
                <CardDescription>Statut des employés cette semaine</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                      <Pie
                      data={presenceDataFromReport}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }: { name?: string; percent?: number }) => `${name ?? ''} ${((percent ?? 0) * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {presenceDataFromReport.map((entry: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Statistiques détaillées</CardTitle>
                <CardDescription>Vue d'ensemble de la présence</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {presenceDataFromReport.map((item: any) => (
                  <div key={item.name} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div 
                          className="h-3 w-3 rounded-full" 
                          style={{ backgroundColor: item.color }}
                        />
                        <span>{item.name}</span>
                      </div>
                      <span>{item.value}%</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full" 
                        style={{ 
                          width: `${item.value}%`,
                          backgroundColor: item.color
                        }}
                      />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="teams" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Performance par équipe</CardTitle>
              <CardDescription>Heures travaillées et moyenne par équipe</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={teamComparisonData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="equipe" type="category" />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="heures" fill="#0f172a" name="Total heures" />
                  <Bar dataKey="moyenne" fill="#9ca3af" name="Moyenne/jour" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trends" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Évolution mensuelle</CardTitle>
              <CardDescription>Tendances des heures travaillées et des retards</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={monthlyTrendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="mois" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Line 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="heures" 
                    stroke="#0f172a" 
                    strokeWidth={2}
                    name="Heures totales"
                  />
                  <Line 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="retards" 
                    stroke="#6b7280" 
                    strokeWidth={2}
                    name="Nombre de retards"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
